import React, { useState } from 'react';
import './FAQ.css';

const faqData = [
  {
    title: 'Interview Assistant',
    questions: [
      { q: 'What is the Interview Assistant?', a: 'Our AI-powered Interview Assistant helps you practice for job interviews with realistic scenarios, personalized feedback, and industry-specific questions to boost your confidence.' },
      { q: 'How does it improve my interview performance?', a: 'It analyzes your responses in real-time, provides constructive feedback, suggests improvements, and helps you develop compelling answers using proven frameworks like STAR method.' },
      { q: 'Can I practice for specific job roles?', a: 'Yes! The assistant tailors questions based on your target role, industry, and experience level, ensuring relevant and challenging practice sessions.' }
    ]
  },
  {
    title: 'Mock Interview Assistant',
    questions: [
      { q: 'How does the Mock Interview Assistant work?', a: 'Our AI generates 15 personalized interview questions based on your job role, experience level, and resume. Each question comes with a full model answer for you to read aloud and record — simulating a real interview experience.' },
      { q: 'How are my recorded answers evaluated?', a: 'After you record all your answers, our Whisper AI transcribes your speech and evaluates each response on content coverage and delivery quality. You receive a score out of 100 per question plus an overall readiness score.' },
      { q: 'What types of questions are included?', a: 'Each session includes a mix of behavioral, technical, situational, and resume-based questions — covering every angle an interviewer might take, tailored specifically to your target role and experience level.' }
    ]
  },
  {
    title: 'Resume Builder',
    questions: [
      { q: 'How does the AI Resume Builder work?', a: 'Our AI guides you through creating a professional resume by suggesting powerful action verbs, optimizing formatting, and ensuring your achievements stand out to recruiters.' },
      { q: 'What formats are supported?', a: 'Export your resume in PDF, DOCX, or plain text formats. All templates are ATS-friendly and professionally designed for maximum impact.' },
      { q: 'Can I create multiple versions?', a: 'Absolutely! Create unlimited resume versions tailored to different job applications, industries, or career stages.' }
    ]
  },
  {
    title: 'Cover Letter Generator',
    questions: [
      { q: 'Why do I need a cover letter?', a: 'Cover letters increase your interview chances by 40%. Our AI creates personalized, compelling narratives that showcase your unique value and genuine interest in the role.' },
      { q: 'How does AI personalization work?', a: 'Simply input the job description and your resume. Our AI analyzes both to craft a customized cover letter that highlights relevant experiences and demonstrates culture fit.' },
      { q: 'What makes it stand out?', a: "We optimize storytelling flow, professional tone, achievement emphasis, keyword integration, and company-specific alignment to create letters that capture recruiters' attention." }
    ]
  },
  {
    title: 'ATS Check',
    questions: [
      { q: 'What is an ATS system?', a: 'Applicant Tracking Systems (ATS) are software used by 98% of Fortune 500 companies to filter resumes. Our tool ensures your resume passes these automated screenings.' },
      { q: 'What does the ATS Check analyze?', a: 'We check formatting compatibility, keyword density, section structure, file type issues, and provide a detailed score with specific recommendations for improvement.' },
      { q: 'How can I improve my ATS score?', a: 'Follow our actionable suggestions: use standard section headings, avoid graphics in text areas, include relevant keywords, and choose ATS-friendly formatting.' }
    ]
  },
  {
    title: 'Interview Questions Generator',
    questions: [
      { q: 'What types of questions are generated?', a: 'We generate behavioral, technical, situational, and role-specific questions based on your target position. From coding challenges to leadership scenarios, covering all interview formats.' },
      { q: 'How are questions personalized?', a: 'Input your job role, industry, and experience level. Our AI generates tailored questions that interviewers actually ask, complete with ideal answer frameworks and evaluation criteria.' },
      { q: 'Can I practice with sample answers?', a: 'Yes! Each question includes example answers, key points to cover, common mistakes to avoid, and tips on how to structure your response for maximum impact.' }
    ]
  },
  {
    title: 'AI Quiz',
    questions: [
      { q: 'What topics can I quiz myself on?', a: 'Choose from technical skills, industry knowledge, behavioral scenarios, or create custom quizzes on any topic relevant to your career goals.' },
      { q: 'How does adaptive difficulty work?', a: 'Our AI adjusts question difficulty based on your performance, focusing on areas needing improvement while building confidence through progressive challenges.' },
      { q: 'Can I track my progress?', a: 'Yes! View detailed analytics showing your strengths, weaknesses, improvement trends, and personalized recommendations for focused learning.' }
    ]
  },
  {
    title: 'General Questions',
    questions: [
      { q: 'Is my data secure and private?', a: 'Absolutely. We use enterprise-grade encryption, never share your data with third parties, and you can delete your information anytime. Your privacy is our priority.' },
      { q: 'Do I need technical skills to use these tools?', a: 'Not at all! Our interface is designed for everyone. Simply input your information, and our AI handles the complexity while providing intuitive, user-friendly experiences.' },
      { q: 'How accurate is the AI?', a: 'Our AI is trained on millions of successful resumes, interviews, and career transitions. It continuously learns and improves, maintaining 95%+ accuracy in recommendations.' },
      { q: 'Can I use multiple products together?', a: 'Yes! Our products are designed to work seamlessly together. Build your resume, tailor it for jobs, check ATS compatibility, and practice interviews all in one platform.' }
    ]
  }
];

const FAQ = () => {
  const [activeCategory, setActiveCategory] = useState(0);
  const [openItems, setOpenItems] = useState({});

  const toggleItem = (index) => {
    setOpenItems(prev => ({ ...prev, [index]: !prev[index] }));
  };

  const handleCategoryChange = (index) => {
    setActiveCategory(index);
    setOpenItems({});
  };

  const current = faqData[activeCategory];

  return (
    <section className="faq-section" id="faq">
      <h1>Frequently Asked Questions</h1>
      <br></br>
      <br></br>

      <div className="faq-layout">

        {/* Sidebar */}
        <nav className="faq-sidebar glass">
          {faqData.map((cat, i) => (
            <div
              key={i}
              className={`faq-sidebar-item ${activeCategory === i ? 'active' : ''}`}
              onClick={() => handleCategoryChange(i)}
            >
              <div className="faq-sidebar-left">
                <span className="faq-sidebar-icon">{cat.icon}</span>
                <div className="faq-sidebar-text">
                  <span className="faq-sidebar-title">{cat.title}</span>
                  <span className="faq-sidebar-count">{cat.questions.length} questions</span>
                </div>
              </div>
              <span className="faq-sidebar-dot" />
            </div>
          ))}
        </nav>

        {/* Main Panel */}
        <div className="faq-panel glass">
          <div className="faq-panel-header">
            <span className="faq-panel-icon">{current.icon}</span>
            <div>
              <h3 className="faq-panel-title">{current.title}</h3>
              <p className="faq-panel-subtitle">{current.questions.length} questions in this section</p>
            </div>
          </div>

          <div className="faq-items">
            {current.questions.map((item, i) => (
              <div
                key={i}
                className={`faq-item ${openItems[i] ? 'active' : ''}`}
                onClick={() => toggleItem(i)}
              >
                <div className="faq-question">
                  <span className="faq-toggle">{openItems[i] ? '−' : '+'}</span>
                  <span className="faq-question-text">{item.q}</span>
                </div>
                <div className="faq-answer">{item.a}</div>
              </div>
            ))}
          </div>
        </div>

      </div>
    </section>
  );
};

export default FAQ;